import { useAppStore } from "@/store/utils/hooks";
import { FC } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { Session } from "@/components/active-session/session";
import { Uuid } from "@/domains/global/identifiers";
import { ZIndex } from "@/domains/design/constants";
import { Dialog } from "react-aria-components";

interface ActiveSessionProps {}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const ActiveSessions: FC<ActiveSessionProps> = observer(() => {
  const { store } = useAppStore();

  const unfinishedSessions = store.sessions.getSessionsExceptByStatus("DISMISSED");
  const importSessions = unfinishedSessions.filter(
    session => session.kind === "IMPORT" || session.kind === "AUTO_ORGANIZE"
  );

  // if there are no processing import sessions, don't render this component
  if (importSessions.length === 0) return null;

  const handleDismissSession = (sessionId: Uuid) => {
    store.sessions.markSessionAsDismissed(sessionId);
  };

  return (
    <StyledSessionDialog>
      {importSessions.map(session => {
        const events = store.sessionEvents.getSessionEventsBySessionId(session.id);
        const status = session.status ?? "PROCESSING";

        return (
          <Session
            key={session.id}
            title={session.title ?? "Untitled"}
            sessionId={session.id}
            status={status}
            events={events}
            onDismiss={() => handleDismissSession(session.id)}
          />
        );
      })}
    </StyledSessionDialog>
  );
});

const StyledSessionDialog = styled(Dialog)`
  position: absolute;
  right: ${({ theme }) => theme.spacing[4]};
  bottom: ${({ theme }) => theme.spacing[4]};
  z-index: ${ZIndex.FloatingTooltip};
  background-color: ${({ theme }) => theme.colors.surface.modal};
  padding-block: ${({ theme }) => theme.spacing[7]};
  padding-inline: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  width: 420px;
`;
